import React, { useState, useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { isMobile } from 'react-device-detect';
import ComponentPalette from './ComponentPalette';
import DropCanvas from './DropCanvas';
import PropertiesPanel from './PropertiesPanel';
import './PageBuilder.css';

// Available component types
const COMPONENT_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  BUTTON: 'button',
  MENU_SECTION: 'menu_section',
  BANNER: 'banner',
  CONTACT_FORM: 'contact_form',
  TWO_COLUMN: 'two_column',
  THREE_COLUMN: 'three_column'
};

function PageBuilder({ pageData = [], onSave }) {
  const [components, setComponents] = useState(pageData);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  const backend = isMobile ? TouchBackend : HTML5Backend;

  // Add component to canvas
  const addComponent = useCallback((componentType, position) => {
    const newComponent = {
      id: Date.now().toString(),
      type: componentType,
      position: position || components.length,
      props: getDefaultProps(componentType),
      style: getDefaultStyle(componentType)
    };
    
    setComponents(prev => [...prev, newComponent]);
  }, [components.length]);

  // Move component within canvas
  const moveComponent = useCallback((dragIndex, hoverIndex) => {
    setComponents(prev => {
      const newComponents = [...prev];
      const draggedComponent = newComponents[dragIndex];
      newComponents.splice(dragIndex, 1);
      newComponents.splice(hoverIndex, 0, draggedComponent);
      return newComponents.map((comp, index) => ({ ...comp, position: index }));
    });
  }, []);

  // Update component properties
  const updateComponent = useCallback((id, updates) => {
    setComponents(prev => 
      prev.map(comp => 
        comp.id === id ? { ...comp, ...updates } : comp
      )
    );
  }, []);

  // Remove component
  const removeComponent = useCallback((id) => {
    setComponents(prev => prev.filter(comp => comp.id !== id));
    setSelectedComponent(null);
  }, []);

  // Save page
  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(components);
    }
  }, [components, onSave]);

  return (
    <DndProvider backend={backend}>
      <div className="page-builder">
        <div className="page-builder-header">
          <div className="header-controls">
            <h2>Page Builder</h2>
            <div className="header-buttons">
              <button 
                className={`btn ${!previewMode ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setPreviewMode(false)}
              >
                <i className="fas fa-edit"></i> Edit
              </button>
              <button 
                className={`btn ${previewMode ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setPreviewMode(true)}
              >
                <i className="fas fa-eye"></i> Preview
              </button>
              <button 
                className="btn btn-success"
                onClick={handleSave}
              >
                <i className="fas fa-save"></i> Save Page
              </button>
            </div>
          </div>
        </div>

        <div className="page-builder-content">
          {!previewMode && (
            <div className="component-palette-sidebar">
              <ComponentPalette 
                componentTypes={COMPONENT_TYPES}
                onAddComponent={addComponent}
              />
            </div>
          )}

          <div className={`canvas-area ${previewMode ? 'preview-mode' : ''}`}>
            <DropCanvas
              components={components}
              onMoveComponent={moveComponent}
              onSelectComponent={setSelectedComponent}
              onRemoveComponent={removeComponent}
              selectedComponent={selectedComponent}
              previewMode={previewMode}
            />
          </div>

          {!previewMode && (
            <div className="properties-panel-sidebar">
              <PropertiesPanel
                selectedComponent={selectedComponent}
                onUpdateComponent={updateComponent}
              />
            </div>
          )}
        </div>
      </div>
    </DndProvider>
  );
}

// Default props for each component type
function getDefaultProps(type) {
  const defaults = {
    [COMPONENT_TYPES.TEXT]: {
      content: 'Your text here...',
      tag: 'p',
      alignment: 'left'
    },
    [COMPONENT_TYPES.IMAGE]: {
      src: '/placeholder.jpg',
      alt: 'Image',
      width: '100%',
      height: 'auto'
    },
    [COMPONENT_TYPES.BUTTON]: {
      text: 'Click Me',
      link: '#',
      variant: 'primary',
      size: 'medium'
    },
    [COMPONENT_TYPES.MENU_SECTION]: {
      title: 'Our Menu',
      categories: ['Breakfast', 'Lunch', 'Dinner']
    },
    [COMPONENT_TYPES.BANNER]: {
      backgroundImage: '/banner-bg.jpg',
      title: 'Welcome',
      subtitle: 'Experience great food',
      buttonText: 'Learn More',
      buttonLink: '#'
    },
    [COMPONENT_TYPES.CONTACT_FORM]: {
      title: 'Contact Us',
      fields: ['name', 'email', 'message'],
      submitText: 'Send Message'
    },
    [COMPONENT_TYPES.TWO_COLUMN]: {
      leftContent: 'Left column content',
      rightContent: 'Right column content'
    },
    [COMPONENT_TYPES.THREE_COLUMN]: {
      leftContent: 'Left column',
      centerContent: 'Center column', 
      rightContent: 'Right column'
    }
  };
  
  return defaults[type] || {};
}

// Default styles for each component type
function getDefaultStyle(type) {
  const defaults = {
    [COMPONENT_TYPES.TEXT]: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem'
    },
    [COMPONENT_TYPES.IMAGE]: {
      marginBottom: '1rem',
      borderRadius: '0px'
    },
    [COMPONENT_TYPES.BUTTON]: {
      padding: '12px 24px',
      borderRadius: '5px'
    },
    [COMPONENT_TYPES.MENU_SECTION]: {
      padding: '3rem 0',
      backgroundColor: '#f8f9fa'
    },
    [COMPONENT_TYPES.BANNER]: {
      minHeight: '400px',
      padding: '4rem 0',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    },
    [COMPONENT_TYPES.CONTACT_FORM]: {
      padding: '2rem',
      backgroundColor: '#ffffff',
      borderRadius: '8px'
    },
    [COMPONENT_TYPES.TWO_COLUMN]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '2rem',
      padding: '2rem 0'
    },
    [COMPONENT_TYPES.THREE_COLUMN]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: '1.5rem',
      padding: '2rem 0'
    }
  };
  
  return defaults[type] || {};
}

export default PageBuilder;